import auth0, { Auth0Result } from 'auth0-js';
import { Observable, Subject } from 'rxjs';

import { isUserAuthenticated } from '@pressreader/authentication-api';
import { configLoaded, getConfigValue } from '@pressreader/config';
import { toCamelCase } from '@pressreader/utils';

import ndAuthentication from 'authentication/nd.authentication';

import { IIdentity } from './adapter';

interface Auth0SSOConfigDTO {
    Enabled: boolean;
    ClientId: string;
    Domain: string;
    ApiUrl: string;
    Scope: string;
    Audience: string;
    ReturnUrl: string;
    LogoutUrl: string;
}

interface Auth0SSOConfig {
    enabled: boolean;
    clientId: string;
    domain: string;
    scope: string;
    audience: string;
    returnUrl: string;
    logoutUrl: string;
}

export class Auth0SSO implements Partial<IIdentity> {
    private readonly loginSuccess = new Subject<unknown>();
    private readonly logoutSuccess = new Subject<void>();
    private api: auth0.WebAuth;
    private auth0Config: Auth0SSOConfig;

    private loadAuth0Config() {
        const cfg = getConfigValue<Auth0SSOConfigDTO>('Authentication.Auth0SSO');
        cfg.Domain ||= cfg.ApiUrl;
        this.auth0Config = toCamelCase(cfg);
    }

    isAuth0Authenticated(auth0Config: Auth0SSOConfig): Promise<Auth0Result | null> {
        return new Promise((resolve, _) => {
            this.api.checkSession(
                {
                    audience: auth0Config.audience,
                    scope: auth0Config.scope,
                    responseType: 'token',
                    redirectUri: auth0Config.returnUrl,
                },
                function (err: unknown, authResult: Auth0Result) {
                    if (!err && authResult) {
                        return resolve(authResult);
                    }
                    return resolve(null);
                },
            );
        });
    }

    async load(): Promise<void> {
        await configLoaded();

        this.loadAuth0Config();
        if (!this.auth0Config?.enabled) {
            return Promise.reject();
        }

        this.api = new auth0.WebAuth({
            domain: this.auth0Config.domain,
            clientID: this.auth0Config.clientId,
        });

        if (!isUserAuthenticated()) {
            const result = await this.isAuth0Authenticated(this.auth0Config);
            if (result) {
                ndAuthentication.signIn();
            }
        }

        return Promise.resolve();
    }

    get enabled() {
        return this.auth0Config?.enabled;
    }

    get onLoginSuccess() {
        return this.loginSuccess.asObservable();
    }

    get onModalClosed() {
        return new Observable<unknown>();
    }

    get onLogoutSuccess() {
        return this.logoutSuccess.asObservable();
    }

    signIn(): void {
        throw new Error('Not Implemented');
    }

    signOut() {
        this.logoutSuccess.next();
        return this.api.client.buildLogoutUrl({
            returnTo: this.auth0Config.logoutUrl,
            clientID: this.auth0Config.clientId,
        });
    }
}
